/**
 * Minimal reactive store. `set` accepts a partial patch or an updater function;
 * subscribers are notified after every applied change. `subscribe` returns an
 * unsubscribe function.
 */
export interface Store<T extends object> {
	get(): T;
	set(patch: Partial<T> | ((prev: T) => Partial<T>)): void;
	subscribe(listener: (state: T) => void): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
	let state: T = { ...initial };
	const listeners = new Set<(state: T) => void>();

	return {
		get(): T {
			return state;
		},
		set(patch): void {
			const next = typeof patch === "function" ? patch(state) : patch;
			state = { ...state, ...next };
			for (const listener of listeners) listener(state);
		},
		subscribe(listener): () => void {
			listeners.add(listener);
			return () => {
				listeners.delete(listener);
			};
		},
	};
}
